/**
 * RecurringSeriesDetailPage - Detail view for a single recurrence series.
 *
 * One component serves both Work Order and Inspection series — the URL
 * pattern selects the type via the route prefix ("recurring-work-orders"
 * vs "recurring-inspections") so the page can resolve the corresponding
 * back-link, occurrence date column, and per-record detail route
 * without an explicit prop.
 *
 * The layout/markup is fully owned by RepeatsDetailTemplate; this page
 * is the controller — loading the series, dispatching mutations, and
 * refetching on success.
 *
 * @route /recurring-work-orders/:id
 * @route /recurring-inspections/:id
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useParams } from 'react-router-dom';
import { recurrenceSeriesService } from '../../services/recurrenceSeriesService';
import type { RecurrenceSeriesRecord, SeriesOccurrence } from '../../services/recurrenceSeriesService';
import { RepeatsDetailTemplate } from '../../components/templates/RepeatsDetailTemplate';
import { qk } from '../../lib/queryKeys';

const RecurringSeriesDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const location = useLocation();
	const isInspections = location.pathname.startsWith('/recurring-inspections');
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: qk.recurrenceSeries.detail(id ?? ''),
		queryFn: async () => {
			const res = await recurrenceSeriesService.getById(id as string);
			return ((res.data as any)?.data ?? null) as RecurrenceSeriesRecord | null;
		},
		enabled: !!id,
	});

	const series = query.data ?? null;
	const isLoading = query.isLoading;
	const error = query.error ? (query.error as Error).message : null;

	/* Invalidate both the specific series detail and every list so the
	   recurring-WO / recurring-inspection list pages reflect the change. */
	const invalidateSeries = () => {
		queryClient.invalidateQueries({ queryKey: qk.recurrenceSeries.all });
		if (id) queryClient.invalidateQueries({ queryKey: qk.recurrenceSeries.detail(id) });
	};

	const cancelSeriesMutation = useMutation({
		mutationFn: () => recurrenceSeriesService.delete(id as string),
		onSuccess: () => {
			invalidateSeries();
			window.history.back();
		},
	});

	const rescheduleMutation = useMutation({
		mutationFn: (vars: { occ: SeriesOccurrence; newDate: string }) =>
			recurrenceSeriesService.rescheduleOccurrence(id as string, vars.occ.id, vars.newDate),
		onSuccess: () => invalidateSeries(),
	});

	const cancelOccurrenceMutation = useMutation({
		mutationFn: (vars: { occ: SeriesOccurrence; reason?: string }) =>
			recurrenceSeriesService.cancelOccurrence(id as string, vars.occ.id, vars.reason),
		onSuccess: () => invalidateSeries(),
	});

	const deleteOccurrenceMutation = useMutation({
		mutationFn: (vars: { occ: SeriesOccurrence }) =>
			recurrenceSeriesService.deleteOccurrence(id as string, vars.occ.id),
		onSuccess: () => invalidateSeries(),
	});

	/* Cancel the entire series — confirm first because this fans out to
	   alerts and hides every future occurrence. */
	const handleCancelSeries = async () => {
		if (!id || !series) return;
		if (!window.confirm('Cancel this recurring schedule? Future occurrences will be hidden and admins assigned to them will be alerted.')) return;
		try {
			await cancelSeriesMutation.mutateAsync();
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to cancel series');
		}
	};

	const handleReschedule = async (occ: SeriesOccurrence, newDate: string) => {
		if (!id) return;
		try {
			await rescheduleMutation.mutateAsync({ occ, newDate });
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to reschedule occurrence');
		}
	};

	const handleCancelOccurrence = async (occ: SeriesOccurrence, reason?: string) => {
		if (!id) return;
		try {
			await cancelOccurrenceMutation.mutateAsync({ occ, reason });
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to cancel occurrence');
		}
	};

	const handleDeleteOccurrence = async (occ: SeriesOccurrence) => {
		if (!id) return;
		try {
			await deleteOccurrenceMutation.mutateAsync({ occ });
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to delete occurrence');
		}
	};

	return (
		<RepeatsDetailTemplate
			className="recurring-series-detail-page"
			dataRef="page-recurring-series-detail-page"
			series={series}
			isLoading={isLoading}
			error={error}
			backTo={isInspections ? '/recurring-inspections' : '/recurring-work-orders'}
			occurrenceRoute={(occId) => isInspections ? `/inspections/${occId}` : `/work-orders/${occId}`}
			dateColumn={isInspections ? 'inspection_date' : 'scheduled_date'}
			onCancelSeries={handleCancelSeries}
			onRescheduleOccurrence={handleReschedule}
			onCancelOccurrence={handleCancelOccurrence}
			onDeleteOccurrence={handleDeleteOccurrence}
		/>
	);
};

export default RecurringSeriesDetailPage;
